import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Divider, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ArrowInstruction from "../../../../components/ArrowInstruction";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import UploadFileArea from "../../../../components/UploadFileArea";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";

const PaloAltoNGFWIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfig, setShowConfig] = useState(true);
  const colors = useColors();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated("paloalto_ngfw");
  const [loading, setLoading] = useState(false);
  const { uploadPaloAltoNGFWFile, registerPaloAltoNGFWIntegration } = useAPI();

  const handleFinishImport = () => {
    if (!selectedFile) {
      Toast.error("Please select a file first");
      return;
    }

    setLoading(true);
    // Create FormData to send the file
    const formData = new FormData();
    formData.append("file", selectedFile);

    // Step 1: Upload the file
    uploadPaloAltoNGFWFile(formData)
      .then((uploadResponse) => {
        const { metadata_file_path } = uploadResponse.data;

        if (!isIntegrated) {
          // Step 2: Register the integration with the metadata file path
          return registerPaloAltoNGFWIntegration({
            metadata_file_path: metadata_file_path,
          });
        }
      })
      .then(() => {
        setShowConfig(false);
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("File uploaded successfully");

        if (!isIntegrated) {
          Toast.success("Palo Alto NGFW integration successful");
        }
      })
      .catch(() => {
        Toast.error({
          text: "Upload failed",
          comment: "Something went wrong. Please try uploading the file again.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFileSelect = (file) => {
    if (file && file.name.endsWith(".xml")) {
      setSelectedFile(file);
    } else if (file) {
      Toast.error("Please select a valid XML file");
    } else {
      setSelectedFile(null);
    }
  };

  const getTitle = () => {
    if (isIntegrated && !guideMode) {
      return "Palo Alto NGFW is integrated";
    }
    return "Import data from Palo Alto Next Generation Firewall";
  };

  const getDescription = () => {
    if (isIntegrated && !guideMode) {
      return "Upload a new configuration file to update your Palo Alto NGFW integration. This will replace the current configuration with the new one.";
    }
    return "To help Breeze detect misconfigurations and security gaps, export and upload your Palo Alto configuration file.";
  };

  const getButtonText = () => {
    if (isIntegrated) {
      return "Update Configuration";
    }
    return "Finish Import Setup";
  };

  return (
    <Box>
      <Guideline
        title={getTitle()}
        image={logos.PaloAltoPrismaLogo}
        name={"Palo Alto NGFW"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode && !showConfig ? null : (
            <Box display="flex" flexDirection="column" gap={3}>
              {guideMode && <IntegrationGuideAlert />}

              <Typography variant={"body1"} color={colors.text_secondary}>
                {getDescription()}
              </Typography>
              <Divider />

              {/* STEP 1 */}
              <Box display="flex" flexDirection="column" gap={1}>
                {/* STEP TITLE */}
                <Box display="flex" flexDirection="column" gap={1}>
                  {(!isIntegrated || guideMode) && (
                    <Typography variant={"body2"} color={colors.text_secondary}>
                      Step 1 of 1
                    </Typography>
                  )}
                  <Typography variant={"subtitle1"}>
                    Export & Upload Configuration File
                  </Typography>
                </Box>

                {/* SUB-STEP 1 */}
                <Box display="flex" flexDirection="column" gap={1} padding={2}>
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Typography variant={"body2"}>1.</Typography>
                    <Typography variant={"body2"}>
                      Export the Configuration File:
                    </Typography>
                  </Box>
                  {/* INSTRUCTIONS */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    paddingLeft={2}
                  >
                    <Typography variant={"body2"} color={colors.text_secondary}>
                      Log in to the firewall console and navigate to:
                    </Typography>

                    {/* STEPS */}
                    <Box display="flex" flexDirection="column">
                      <ArrowInstruction>
                        <Typography variant={"subtitle2"}>
                          Device tab
                        </Typography>
                      </ArrowInstruction>
                      <ArrowInstruction>
                        <Typography variant={"subtitle2"}>
                          Setup{" "}
                          <Typography component="span" variant={"body2"}>
                            (left menu)
                          </Typography>
                        </Typography>
                      </ArrowInstruction>
                      <ArrowInstruction>
                        <Typography variant={"subtitle2"}>
                          Operations{" "}
                          <Typography component="span" variant={"body2"}>
                            tab
                          </Typography>
                        </Typography>
                      </ArrowInstruction>
                      <ArrowInstruction>
                        <Typography variant={"body2"}>
                          Then under{" "}
                          <Typography component="span" variant={"subtitle2"}>
                            Configuration Management
                          </Typography>
                        </Typography>
                      </ArrowInstruction>
                      <ArrowInstruction>
                        <Typography variant={"body2"}>
                          Click{" "}
                          <Typography component="span" variant={"subtitle2"}>
                            Export Named Configuration Snapshot
                          </Typography>
                        </Typography>
                      </ArrowInstruction>
                      <ArrowInstruction>
                        <Typography variant={"body2"}>Select</Typography>
                        <Box
                          display="flex"
                          flexDirection="row"
                          width="fit-content"
                          sx={{
                            border: `1px solid ${colors.text_area_outlined_enabled_stroke}`,
                            backgroundColor:
                              colors.text_area_outlined_enabled_value_bg,
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            color={colors.text_primary}
                            sx={{
                              // TODO: uncomment this when we have the font
                              // fontFamily: "DM Mono",
                              fontSize: 14,
                              fontWeight: 400,
                              padding: "8px 24px 8px 14px",
                            }}
                          >
                            running-config.xml
                          </Typography>
                        </Box>
                      </ArrowInstruction>
                      <ArrowInstruction>
                        <Typography variant={"body2"}>
                          Click{" "}
                          <Typography component="span" variant={"subtitle2"}>
                            OK
                          </Typography>
                        </Typography>
                      </ArrowInstruction>
                    </Box>

                    <Typography variant={"body2"} color={colors.text_help}>
                      This generates the file needed for upload.
                    </Typography>
                  </Box>
                </Box>

                {/* SUB-STEP 2 */}
                <Box display="flex" flexDirection="column" gap={1} padding={2}>
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Typography variant={"body2"}>2.</Typography>
                    <Typography variant={"body2"}>
                      Upload the XML file:
                    </Typography>
                  </Box>
                  {/* INSTRUCTIONS */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    paddingLeft={2}
                  >
                    <UploadFileArea
                      onFileSelect={handleFileSelect}
                      selectedFile={selectedFile}
                      disabled={guideMode}
                      label={
                        <Box display="flex" alignItems="center" gap="6px">
                          <SettingsOutlinedIcon
                            sx={{ fontSize: 18, color: colors.grey_700 }}
                          />
                          <Typography
                            variant={"body2"}
                            color={colors.text_secondary}
                          >
                            Configurations
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Box>

              <Divider />

              <ContainedButton
                onClick={handleFinishImport}
                color="primary"
                size="medium"
                disabled={!selectedFile || loading}
              >
                {loading ? <LoadingIcon size={20} /> : getButtonText()}
              </ContainedButton>
            </Box>
          )
        }
      />
    </Box>
  );
};

export default PaloAltoNGFWIntegration;
